// ITEM 6 — THEMES. **THE ONE LAW**, imported by the CLIENT (which paints it) and by the SERVER
// (which is asked to store it), so "light" means the same thing on both sides of the wire.
//
// Her order: LIGHT per her image — white background, black letters, gold and purple decor — AND the
// DARK house theme, member's choice in My Room settings, PERSISTED ON THE ACCOUNT, and the inbox and
// its threads honour it. Site-wide is the FUTURE ROUND she herself declared it to be (LR15-2).
//
// THE FOUR DECISIONS IN HERE, each of which this repo has already paid for once somewhere else:
//
//   1. **THE ATTRIBUTE IS WRITTEN FOR BOTH THEMES, NEVER ONLY FOR LIGHT.** If dark were "the absence
//      of `data-theme`" then a member who CHOSE dark and a member whose preference has not loaded
//      yet are the same DOM, and nothing — no gate, no walkthrough, no future surface — can tell an
//      answer from a silence. That is R18's distinction exactly: a value the account DELIBERATELY
//      carries is an ANSWER; a missing one is not.
//
//   2. **AN UNKNOWN THEME IS REFUSED, NOT REPLACED.** R44's petition rosters were allow-lists with a
//      SILENT fallback, so a pick the two sides disagreed about was not refused — it was swapped,
//      and she found out by reading her own petition. `isTheme` is the roster and the server answers
//      a value that is not on it rather than storing the default in its place.
//
//   3. **THE PALETTE IS DECLARED ONCE, HERE, AND THE STYLESHEET IS HELD TO IT** (R13/R14/R15: a law
//      that crosses a boundary must open BOTH sides). `LIGHT_PALETTE` is the roster; the
//      `[data-theme="light"]` block in `src/styles.css` declares exactly these custom properties and
//      the fast gate asserts the two agree key for key, both directions. Every other light rule uses
//      `var(--lt-*)`, so there is no second copy of a colour anywhere.
//
//   4. **A THEME THAT CANNOT BE READ IS NOT A THEME.** `LIGHT_CONTRAST_PAIRS` names every place ink
//      meets a surface and the floor it must clear, and `contrastRatio` is arithmetic the gate RUNS
//      over the declared palette. Gold on white is the trap: the house gold (#d8a94b) is beautiful
//      and carries 1.9:1 as lettering on white, so this theme has TWO golds with their jobs named —
//      one that FILLS (dark ink on it) and one that WRITES.

// The whole roster. A theme not on this list does not exist.
export const THEMES = ["dark", "light"];
export const DEFAULT_THEME = "dark";

// Where it lives, in the three places it has to live.
export const THEME_STATE_KEY = "theme";        // the key inside `profiles.room_state` (a jsonb MERGE)
export const THEME_STORAGE_KEY = "12h_theme";  // the device cache, read BEFORE first paint
export const THEME_ATTR = "data-theme";        // the attribute on <html> the stylesheet keys off

export const THEME_LABELS = { dark: "Dark", light: "Light" };
export const THEME_NOTES = {
  dark: "The house as you know it — black and gold.",
  light: "White paper, black ink, gold and purple.",
};
export const THEME_HEADING = "Your theme";
export const THEME_SCOPE_NOTE = "Your inbox and your conversations follow this.";

export function isTheme(value) {
  return typeof value === "string" && THEMES.includes(value);
}

// For a READER (a paint, a boot, a render): anything unrecognised is the house default, because a
// screen must always have a colour. The WRITER does not use this — see the server route, which
// refuses instead of replacing.
export function normalizeTheme(value) {
  return isTheme(value) ? value : DEFAULT_THEME;
}

export function themeLabel(theme) {
  return THEME_LABELS[normalizeTheme(theme)];
}

export function themeNote(theme) {
  return THEME_NOTES[normalizeTheme(theme)];
}

// What a `room_state` payload says her theme is. One reader, so the boot, the room and the walk
// cannot disagree about where the key lives.
export function themeFromState(state) {
  return normalizeTheme(state && typeof state === "object" ? state[THEME_STATE_KEY] : null);
}

// THE PAINT. Takes anything with `setAttribute` — a real `<html>`, or a fake element a gate hands
// it — and returns the theme it actually applied, so a caller never has to normalize twice.
export function applyTheme(root, theme) {
  const next = normalizeTheme(theme);
  if (root && typeof root.setAttribute === "function") root.setAttribute(THEME_ATTR, next);
  return next;
}

// THE DEVICE CACHE, and it is a CACHE — the account is the truth. It exists for one reason: the
// account read is a round trip, and without a value at first paint every light-theme member gets a
// black flash on every page load. Both sides swallow their own errors: Safari private mode throws on
// `localStorage`, and a theme is never worth a blank screen.
export function readStoredTheme(storage) {
  try {
    return normalizeTheme(storage && storage.getItem ? storage.getItem(THEME_STORAGE_KEY) : null);
  } catch {
    return DEFAULT_THEME;
  }
}

export function storeTheme(storage, theme) {
  const next = normalizeTheme(theme);
  try {
    if (storage && storage.setItem) storage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    /* a theme is never worth a thrown error */
  }
  return next;
}

// ---------------------------------------------------------------------------------------------
// THE LIGHT PALETTE — HER FOUR COLOURS, NAMED BY THE JOB EACH ONE DOES.
// White background, black letters, gold and purple decor. The stylesheet declares exactly these
// custom properties under `[data-theme="light"]` and uses nothing else.
// ---------------------------------------------------------------------------------------------
export const LIGHT_PALETTE = {
  "--lt-bg": "#ffffff",
  "--lt-surface": "#fbf8f3",
  "--lt-ink": "#171013",
  "--lt-muted": "#5b5158",
  // TWO GOLDS, and the reason is contrast, not taste: `--lt-gold-fill` is the house gold and it
  // FILLS (dark ink sits on it at 8.7:1); `--lt-gold` is darkened until it can WRITE on white.
  "--lt-gold": "#8a6412",
  "--lt-gold-fill": "#d8a94b",
  "--lt-gold-line": "rgba(138, 100, 18, 0.42)",
  "--lt-gold-soft": "rgba(216, 169, 75, 0.16)",
  "--lt-purple": "#5b2a86",
  "--lt-purple-line": "rgba(91, 42, 134, 0.34)",
  "--lt-purple-soft": "rgba(91, 42, 134, 0.09)",
  "--lt-on-gold": "#14110b",
  "--lt-on-purple": "#ffffff",
  "--lt-shadow": "0 14px 38px rgba(23, 16, 19, 0.14)",
};

// Every place ink meets a surface in the light theme, and the floor it must clear. 4.5 is the body
// floor; her own words get 7, because a message from somebody is the thing she came here to read.
export const LIGHT_CONTRAST_PAIRS = [
  { name: "her words on the page", fg: "--lt-ink", bg: "--lt-bg", floor: 7 },
  { name: "her words on a card", fg: "--lt-ink", bg: "--lt-surface", floor: 7 },
  { name: "a message preview", fg: "--lt-muted", bg: "--lt-bg", floor: 4.5 },
  { name: "gold lettering", fg: "--lt-gold", bg: "--lt-bg", floor: 4.5 },
  { name: "purple lettering", fg: "--lt-purple", bg: "--lt-bg", floor: 4.5 },
  { name: "the label on a gold button", fg: "--lt-on-gold", bg: "--lt-gold-fill", floor: 4.5 },
  { name: "the label on a purple badge", fg: "--lt-on-purple", bg: "--lt-purple", floor: 4.5 },
];

// WCAG relative luminance and the contrast ratio between two opaque colours. Hex only, on purpose:
// a ratio computed against a translucent rgba is a ratio against a colour nobody can see, and every
// pair above is deliberately declared in hex for exactly that reason.
export function parseHex(hex) {
  const m = typeof hex === "string" ? hex.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i) : null;
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].split("").map((c) => c + c).join("") : m[1];
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

export function relativeLuminance(hex) {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

// The pairs that FAIL their floor, named. Empty is the only shipping answer.
export function contrastViolations(palette = LIGHT_PALETTE, pairs = LIGHT_CONTRAST_PAIRS) {
  const out = [];
  for (const pair of pairs) {
    const ratio = contrastRatio(palette[pair.fg], palette[pair.bg]);
    if (ratio === null) {
      out.push({ ...pair, ratio: null, issue: `${pair.fg} or ${pair.bg} is not an opaque hex colour` });
    } else if (ratio < pair.floor) {
      out.push({ ...pair, ratio: Number(ratio.toFixed(2)), issue: `${pair.name} reads at ${ratio.toFixed(2)}:1, under ${pair.floor}:1` });
    }
  }
  return out;
}
